import { IconChevronRight } from '@tabler/icons-react';
import { Fragment } from 'react';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { useTranslation } from '../../i18n/LocaleProvider';
import { Link } from '../display/Link';

export interface BreadcrumbItem {
  label: ReactNode;
  /** Omitted on the final crumb — the current page is not a link. */
  href?: string;
  icon?: ReactNode;
}

export interface BreadcrumbProps extends Omit<ComponentPropsWithRef<'nav'>, 'children'> {
  items: BreadcrumbItem[];
  /** Replaces the default chevron. Marked decorative either way. */
  separator?: ReactNode;
  /** Accessible name for the landmark. */
  label?: string;
}

export function Breadcrumb({
  items,
  separator,
  label,
  className,
  ...rest
}: BreadcrumbProps) {
  const { t } = useTranslation();

  /*
     The landmark's own name is interface text like any other: a hardcoded
     English one is read aloud in English on a Portuguese page, and no language
     change can reach it because there is no key for it to find.
  */
  const name = label ?? t('ds.breadcrumb.label');

  return (
    <nav {...rest} aria-label={name} className={cn('min-w-0', className)}>
      <ol className="flex list-none flex-wrap items-center gap-x-2 gap-y-1 p-0">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          // Positional identity plus whatever the item carries — never a bare
          // index, and never just the href, which two crumbs could share.
          const key = `${index}-${item.href ?? ''}`;

          return (
            <Fragment key={key}>
              {index > 0 ? (
                <li aria-hidden="true" className="shrink-0 text-muted">
                  {separator ?? <IconChevronRight className="size-3.5" stroke={2} />}
                </li>
              ) : null}

              <li className="flex min-w-0 items-center gap-1.5">
                {item.icon ? (
                  <span
                    aria-hidden="true"
                    // `[&>svg]:size-full`: the slot sizes the glyph, not the
                    // caller. `icon` is a `ReactNode` and a bare Tabler glyph
                    // defaults to 24px, which inside a 14px grid track overflows
                    // 5px below the label it sits beside.
                    className="grid size-3.5 shrink-0 place-items-center text-muted [&>svg]:size-full"
                  >
                    {item.icon}
                  </span>
                ) : null}

                {isLast || !item.href ? (
                  <span
                    aria-current={isLast ? 'page' : undefined}
                    className={cn(
                      'min-w-0 truncate text-xs',
                      isLast ? 'font-semibold text-heading' : 'text-muted',
                    )}
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link href={item.href} variant="muted" className="min-w-0 truncate text-xs">
                    {item.label}
                  </Link>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
